import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSchedulerV3Service, serviceName } from '../service';
import { ListWaitlistAvailabilitiesIO, ServiceQueries } from '../types';
import { useSchedulerV3Queries } from './use-scheduler-v3-queries';
import { UseSchedulerV3QueryEndpointArgs, useSchedulerV3Query } from './use-scheduler-v3-query';

/**
 * A hook that returns a query for the `ListWaitlistAvailabilities` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListWaitlistAvailabilitiesQuery = <E = unknown, D = ListWaitlistAvailabilitiesIO['output']>(
  args: UseSchedulerV3QueryEndpointArgs<'ListWaitlistAvailabilities', E, D>
) =>
  useSchedulerV3Query({
    endpointName: 'ListWaitlistAvailabilities',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `ListWaitlistAvailabilities` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListWaitlistAvailabilitiesQueries = <E = unknown, D = ListWaitlistAvailabilitiesIO['output']>(
  argsArr: UseSchedulerV3QueryEndpointArgs<'ListWaitlistAvailabilities', E, D>[]
) =>
  useSchedulerV3Queries(
    argsArr.map((args) => ({
      endpointName: 'ListWaitlistAvailabilities',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `ListWaitlistAvailabilities` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchListWaitlistAvailabilities = () => {
  const queryClient = useQueryClient();

  const fetchListWaitlistAvailabilities = async <E = unknown, D = ListWaitlistAvailabilitiesIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSchedulerV3QueryEndpointArgs<'ListWaitlistAvailabilities', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      ListWaitlistAvailabilitiesIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListWaitlistAvailabilities'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        ListWaitlistAvailabilitiesIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListWaitlistAvailabilities'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'ListWaitlistAvailabilities'>({
          serviceName,
          endpointName: 'ListWaitlistAvailabilities',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaSchedulerV3Service.ListWaitlistAvailabilities(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchListWaitlistAvailabilities;
};
